import { type CSSProperties, useCallback, useMemo, useRef } from 'react';
import hljs from 'highlight.js/lib/core';
import json from 'highlight.js/lib/languages/json';

import { getActiveOverriddenLines, type OverriddenLinesSnapshot } from '../../utils/jsonPathLineResolver';

import 'highlight.js/styles/atom-one-dark.css';

hljs.registerLanguage('json', json);

interface JsonEditorProps {
    value: string;
    onChange: (value: string) => void;
    /**
     * Snapshot (line number -> original line text) of the fields that were substituted from
     * vehicle overrides when the payload was loaded. A line stays highlighted in the gutter
     * only while its text still matches the snapshot, so editing one tagged line no longer
     * clears every other tag in the payload.
     */
    overriddenLinesSnapshot?: OverriddenLinesSnapshot;
}

const GUTTER_WIDTH = 44;

const sharedStyle: CSSProperties = {
    margin: 0,
    padding: 16,
    fontFamily: 'monospace',
    fontSize: 14,
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    border: 'none',
    outline: 'none',
    tabSize: 2,
    letterSpacing: 'normal',
    wordSpacing: 'normal',
};

const JsonEditor = ({ value, onChange, overriddenLinesSnapshot }: JsonEditorProps) => {
    const preRef = useRef<HTMLPreElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const gutterRef = useRef<HTMLDivElement>(null);
    const highlighted = hljs.highlight(value || ' ', { language: 'json' }).value;

    const lineCount = useMemo(() => (value || ' ').split('\n').length, [value]);
    const highlightedLines = useMemo(
        () =>
            overriddenLinesSnapshot && overriddenLinesSnapshot.size > 0
                ? getActiveOverriddenLines(value, overriddenLinesSnapshot)
                : new Set<number>(),
        [value, overriddenLinesSnapshot],
    );

    const syncScroll = useCallback(() => {
        if (textareaRef.current && preRef.current) {
            preRef.current.scrollTop = textareaRef.current.scrollTop;
            preRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
        if (textareaRef.current && gutterRef.current) {
            gutterRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    }, []);

    return (
        <div
            style={{
                position: 'relative',
                display: 'flex',
                backgroundColor: '#282c34',
                borderRadius: 4,
                flex: '1 1 0',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <div
                ref={gutterRef}
                aria-hidden="true"
                style={{
                    width: GUTTER_WIDTH,
                    flex: '0 0 auto',
                    overflow: 'hidden',
                    paddingTop: 16,
                    paddingBottom: 16,
                    backgroundColor: '#21252b',
                    borderRight: '1px solid #3a3f4b',
                    textAlign: 'right',
                }}
            >
                {Array.from({ length: lineCount }, (_, i) => i + 1).map((lineNumber) => (
                    <div
                        key={lineNumber}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: 14,
                            lineHeight: '1.5',
                            paddingRight: 8,
                            color: highlightedLines.has(lineNumber) ? '#e68036' : '#6b7280',
                            backgroundColor: highlightedLines.has(lineNumber) ? '#f7dcc4' : 'transparent',
                            fontWeight: highlightedLines.has(lineNumber) ? 'bold' : 'normal',
                        }}
                        title={
                            highlightedLines.has(lineNumber)
                                ? 'Value overridden by the configured message this payload was based on'
                                : undefined
                        }
                    >
                        {lineNumber}
                    </div>
                ))}
            </div>
            <div style={{ position: 'relative', flex: '1 1 0', minWidth: 0 }}>
                <pre
                    ref={preRef}
                    aria-hidden="true"
                    style={{
                        ...sharedStyle,
                        pointerEvents: 'none',
                        background: 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <code
                        className="hljs language-json"
                        style={{
                            display: 'block',
                            background: 'none',
                            padding: 0,
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                            fontFamily: 'inherit',
                        }}
                        dangerouslySetInnerHTML={{ __html: highlighted + '<br />' }}
                    />
                </pre>
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onScroll={syncScroll}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    data-gramm="false"
                    style={{
                        ...sharedStyle,
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        resize: 'none',
                        color: 'transparent',
                        caretColor: '#c5c8c6',
                        backgroundColor: 'transparent',
                        WebkitTextFillColor: 'transparent',
                        overflow: 'auto',
                    }}
                />
            </div>
        </div>
    );
};

export default JsonEditor;
